import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { BookingLifecycle } from '../types/bookings';

export const bookingLifecycleLabels: Record<keyof BookingLifecycle, string> = {
    created: 'Booking created', paymentPending: 'Payment pending', paid: 'Payment received',
    confirmed: 'Booking confirmed', rescheduled: 'Booking rescheduled', cancelled: 'Booking cancelled',
    abandoned: 'Booking abandoned (payment not completed)',
    completed: 'Booking completed', prescriptionUploaded: 'Prescription uploaded',
    reviewFeedbackRequested: 'Review / feedback requested', refunded: 'Refund processed',
    collectorAssigned: 'Sample collector assigned', sampleCollected: 'Sample collected',
    patientVisited: 'Patient visited', reportAdded: 'Report added', reportDelivered: 'Report delivered',
};

export const getRecordedLifecycleEvents = (
    lifecycle?: BookingLifecycle,
    createdAt?: string | Date
) => {
    const recorded = { ...lifecycle };
    if (!recorded.created && createdAt && Number.isFinite(new Date(createdAt).getTime())) {
        recorded.created = { at: new Date(createdAt).toISOString() };
    }
    return (Object.keys(bookingLifecycleLabels) as (keyof BookingLifecycle)[])
        .flatMap(key => {
            const event = recorded[key];
            return event && Number.isFinite(Date.parse(event.at)) ? [{ key, ...event }] : [];
        })
        .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
};

export default function BookingCycle({ lifecycle, createdAt, title = 'Booking cycle', external = false }: {
    lifecycle?: BookingLifecycle; createdAt?: string | Date; title?: string; external?: boolean;
}) {
    const events = getRecordedLifecycleEvents(lifecycle, createdAt);
    const formatTime = (at: string) => new Date(at).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    return (
        <Stack spacing={1.5}>
            <Typography variant="subtitle1" component="h3" fontWeight={600}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
                {external ? 'First recorded milestones are shown below. Earlier events may be missing for older bookings.' : 'First recorded milestones are shown below.'} Times are in IST.
            </Typography>
            {events.length === 0 ? <Typography color="text.secondary">No booking-cycle history has been recorded.</Typography> :
                <Box component="ol" aria-label={title} sx={{ m: 0, pl: 3 }}>
                    {events.map(event => <Box component="li" key={event.key} sx={{ pl: 1, mb: 2 }}>
                        <Typography fontWeight={600}>{bookingLifecycleLabels[event.key]}</Typography>
                        <Typography variant="body2" color="text.secondary" component="time" dateTime={event.at}>{formatTime(event.at)}</Typography>
                        {event.by && <Typography variant="body2">By: {event.by}</Typography>}
                        {event.amount != null && <Typography variant="body2">Amount: ₹{event.amount.toLocaleString('en-IN')}</Typography>}
                        {event.reason && <Typography variant="body2">Reason: {event.reason}</Typography>}
                    </Box>)}
                </Box>}
        </Stack>
    );
}
